import { PASSWORD_MIN } from '@fleequid-secrets/shared';
import { type ReactNode, useState } from 'react';
import { api, messageOf } from '@/api/client';
import { useOverlay } from '@/lib/overlay';
import { Icon } from './Icon';

// One's own password: the current one, then the new one twice. Every other session ends.
export function PasswordDialog(props: { onClose: () => void; onDone: () => void }): ReactNode {
    useOverlay();
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [again, setAgain] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const problem =
        next.length > 0 && next.length < PASSWORD_MIN
            ? `${PASSWORD_MIN} characters or more`
            : again && again !== next
              ? 'The two do not match'
              : null;

    return (
        <div className="scrim" onMouseDown={(event) => event.target === event.currentTarget && props.onClose()}>
            <form
                className="dialog float"
                onSubmit={(event) => {
                    event.preventDefault();
                    if (problem || !current || !next || next !== again) {
                        return;
                    }
                    setBusy(true);
                    setError(null);
                    api.changePassword(current, next)
                        .then(props.onDone)
                        .catch((reason: unknown) => setError(messageOf(reason)))
                        .finally(() => setBusy(false));
                }}
            >
                <div className="sheet-head">
                    <div>
                        <h2>Change my password</h2>
                        <p>Your other sessions will sign out.</p>
                    </div>
                    <button type="button" className="closebtn" onClick={props.onClose} title="Close (esc)">
                        <Icon name="x" size={17} />
                    </button>
                </div>
                <div className="dialog-body">
                    <label>
                        <span className="micro">Current password</span>
                        <input
                            type="password"
                            autoComplete="current-password"
                            value={current}
                            onChange={(event) => setCurrent(event.target.value)}
                            autoFocus
                        />
                    </label>
                    <label>
                        <span className="micro">New password · {PASSWORD_MIN} characters or more</span>
                        <input type="password" autoComplete="new-password" value={next} onChange={(event) => setNext(event.target.value)} />
                    </label>
                    <label>
                        <span className="micro">New password, again</span>
                        <input type="password" autoComplete="new-password" value={again} onChange={(event) => setAgain(event.target.value)} />
                    </label>
                    {(problem || error) && <div className="form-error">{problem ?? error}</div>}
                    <div className="dialog-actions">
                        <button type="button" className="act" onClick={props.onClose}>
                            Cancel
                        </button>
                        <button className="act primary" disabled={busy || Boolean(problem) || !current || !next || next !== again}>
                            {busy ? 'Saving…' : 'Change it'}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}
